
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPosts, Post } from "@/lib/supabase";
import { formatDate } from "@/lib/utils";
import { Timeline, TimelineItem } from "@/components/Timeline";
import { Calendar } from "lucide-react";

const Archive = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [yearGroups, setYearGroups] = useState<Record<string, Post[]>>({});

  useEffect(() => {
    const fetchPosts = async () => {
      setIsLoading(true);
      const data = await getPosts();
      setPosts(data);
      
      // Group posts by year
      const groups: Record<string, Post[]> = {};
      data.forEach(post => {
        const year = new Date(post.created_at).getFullYear().toString();
        if (!groups[year]) {
          groups[year] = [];
        }
        groups[year].push(post);
      });
      
      // Sort years in descending order
      const sortedGroups: Record<string, Post[]> = {};
      Object.keys(groups)
        .sort((a, b) => parseInt(b) - parseInt(a))
        .forEach(year => {
          sortedGroups[year] = groups[year].sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        });
      
      setYearGroups(sortedGroups);
      setIsLoading(false);
    };
    
    fetchPosts();
  }, []);

  return (
    <div className="container py-12 max-w-screen-xl">
      <div className="mb-12">
        <h1 className="mb-6 text-4xl font-bold">Archive</h1>
        <p className="text-lg text-muted-foreground">
          Browse all articles published on AI Genesis chronologically
        </p>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blog-neon border-t-transparent shadow-[0_0_10px_rgba(67,255,255,0.5)]"></div>
        </div>
      ) : Object.keys(yearGroups).length > 0 ? (
        <div className="space-y-12">
          {Object.entries(yearGroups).map(([year, yearPosts]) => (
            <div key={year} className="space-y-6">
              <div className="flex items-center gap-2">
                <Calendar className="h-6 w-6 text-blog-neon" />
                <h2 className="text-2xl font-bold">{year}</h2>
              </div>
              <Timeline>
                {yearPosts.map(post => (
                  <TimelineItem key={post.id}>
                    <div className="space-y-1.5">
                      <div className="text-sm text-muted-foreground">
                        {formatDate(post.created_at)}
                      </div>
                      <Link 
                        to={`/post/${post.slug}`}
                        className="block text-xl font-medium hover:text-blog-neon transition-colors"
                      >
                        {post.title}
                      </Link>
                      <p className="text-muted-foreground line-clamp-2">
                        {post.excerpt || post.content.substring(0, 150).replace(/<[^>]*>/g, '')}...
                      </p>
                    </div>
                  </TimelineItem>
                ))}
              </Timeline>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-12 text-center glass-card p-8">
          <h2 className="text-2xl font-medium text-gradient">No posts found</h2>
          <p className="mt-2 text-muted-foreground">Check back soon for new content!</p>
        </div>
      )}
    </div>
  );
};

export default Archive;
